/* eslint-disable no-useless-catch */
import { apiClient } from "../../api/client";

import { getAll, saveItem, deleteItem } from "../../localdb/indexeddb";


export interface HtmlFileId {
  timestamp: number;
  creationTime: string;
}

export interface Template {
  id: string;
  isAcroFile: boolean;
  isDocxFile: boolean;
  userEmail: string;
  fileTitle: string | null;
  fileName: string;
  createdAt: string;
  isPublished: boolean;
  isPending: boolean;
  cacheId?: string;
  memberId: string;
  version?: string;
  /**
   * Optional last-updated timestamp returned by backend.
   * Used for sorting when present; falls back to createdAt.
   */
  updatedAt?: string;
  fileId: string;
  htmlFileId: HtmlFileId;
  htmlId?: string;
  /**
   * Added client-side default field to drive UI until backend provides it.
   * Always set to "Published" by fetchTemplates() for now.
   */
  published?: string;
  pdfType?: string;
}

export interface TemplatesResponse {
  message: string;
  count: number;
  data: Template[];
}

// Member-assigned templates API response (keep fields optional to avoid conflicts)
// Member templates now use the same Template/TemplatesResponse types

export interface TemplateDetails {
  fileId: string;
  userEmail: string;
  isPublished: boolean;
  htmlContent: string;
  fileTitle: string | null;
  fileName: string;
  /**
   * Optional field containing prefill values for form sections.
   * Supports both flat structure (backwards compatible) and nested structure:
   * - Flat: { "0": {...formValues}, "1_2": {...formValues} }
   * - Nested: { "0": {...formValues}, "1": { "2": {...formValues} } }
   * The component will normalize flat structure to nested on initialization.
   */
  sectionInputs?: {
    [key: string]:
    | { [name: string]: string }
    | { [subsectionKey: string]: { [name: string]: string } };
  };
}

export interface TemplateDetailsResponse {
  message: string;
  data: TemplateDetails;
}

export interface TemplateFormDataResponse {
  message: string;
  fileId: string;
  data: {
    formData: string;
  };
}

export interface SaveFormResponse {
  message: string;
  fileId: string;
}

/**
 * Persists filled form data.
 * Sends a POST to /save-form with sectionInput payload and returns the response body.
 */




export async function saveForm(
  fileId: string,
  sectionInput: TemplateDetails["sectionInputs"] | Record<string, unknown>,
  emailId: string,
  memberId: string
): Promise<SaveFormResponse> {
  try {

    const payload = { data: sectionInput, emailId, memberId };
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (false) {
      await SaveFormData(payload, fileId);
      return {
        message: "HTML info updated in local storage successfully.",
        fileId,
      };
    } else {
      const response = await apiClient.patch(
        `/PDFConvertor/reformed/html/info/${encodeURIComponent(fileId)}`,
        payload
      );
      const backendMessage =
        (response?.data && (response.data as { message?: string }).message) || "";

      return {
        message: backendMessage || "HTML info updated successfully.",
        fileId,
      };
    }



  } catch (error) {

    throw error;
  }
}


export async function saveOfflineForm(
  fileId: string,
  payload: any
): Promise<SaveFormResponse> {
  try {
    const response = await apiClient.patch(
      `/PDFConvertor/reformed/html/info/${encodeURIComponent(fileId)}`,
      payload
    );
    const backendMessage =
      (response?.data && (response.data as { message?: string }).message) || "";

    return {
      message: backendMessage || "HTML info updated successfully.",
      fileId,
    };
  } catch (error) {

    throw error;
  }
}

export async function submitXFAForm(
  fileId: string,
  sectionInput: TemplateDetails["sectionInputs"] | Record<string, unknown>,
  isAdminSubmit: boolean = false
): Promise<SaveFormResponse> {
  try {
    const payload = { fileId, filledValues: sectionInput };
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (false) {
      await SubmitFormData(payload, fileId, "xfa")
      return {
        message: "Form submitted successfully.",
        fileId,
      }
    }
    const response = await apiClient.post<Blob>(
      `/PDFConvertor/fill`,
      payload,
      { responseType: "blob" }
    );
    if (true) {
      const blob = response?.data;
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        // Try to extract filename from Content-Disposition header
        const dispo = (response.headers as Record<string, string | undefined>)["content-disposition"];
        let fileName = `filled-${fileId}.pdf`;
        if (dispo) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(dispo);
          const name = match?.[1] || match?.[2];
          if (name) fileName = decodeURIComponent(name);
        }
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return {
          message: `Downloaded ${fileName}`,
          fileId,
        };
      }
      return {
        message: "Form submitted, but no file received.",
        fileId,
      }
    }
    return {
      message: "Form submitted successfully.",
      fileId,
    }

  } catch (error) {

    throw error;
  }
}

export async function submitXFAFormOffline(
  fileId: string,
  payload: any,
  isAdminSubmit: boolean = false
): Promise<SaveFormResponse> {
  try {

    const response = await apiClient.post<Blob>(
      `/PDFConvertor/fill`,
      payload,
      { responseType: "blob" }
    );
    if (isAdminSubmit) {
      const blob = response?.data;
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        // Try to extract filename from Content-Disposition header
        const dispo = (response.headers as Record<string, string | undefined>)["content-disposition"];
        let fileName = `filled-${fileId}.pdf`;
        if (dispo) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(dispo);
          const name = match?.[1] || match?.[2];
          if (name) fileName = decodeURIComponent(name);
        }
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return {
          message: `Downloaded ${fileName}`,
          fileId,
        };
      }
      return {
        message: "Form submitted, but no file received.",
        fileId,
      }
    }
    return {
      message: "Form submitted successfully.",
      fileId,
    }

  } catch (error) {

    throw error;
  }
}




export async function submitForm(
  fileId: string,
  sectionInput: TemplateDetails["sectionInputs"] | Record<string, unknown>,
  isAdminSubmit: boolean = false
): Promise<SaveFormResponse> {
  try {
    // const payload = { fileId, filledValues: sectionInput };
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (false) {
      await SubmitFormData(encodeURIComponent(JSON.stringify(sectionInput)), fileId, "acro")
      return {
        message: "Form submitted successfully.",
        fileId,
      }
    } else {
      const ACRO_INTELLIGENCE_URL = import.meta.env.VITE_PDF_INTELLIGENCE_URL || 'https://pdf-intelligence-kit.genesis.dev.platform.autonomize.ai';

      const response = await apiClient.post<Blob>(
        `${ACRO_INTELLIGENCE_URL}/api/submit/${encodeURIComponent(fileId)}`,
        sectionInput,
        { responseType: "blob" }
      );

      if (true) {
        const blob = response?.data;
        if (blob && blob.size > 0) {
          const url = window.URL.createObjectURL(blob);
          // Try to extract filename from Content-Disposition header
          const dispo = (response.headers as Record<string, string | undefined>)["content-disposition"];
          let fileName = `filled-${fileId}.pdf`;
          if (dispo) {
            const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(dispo);
            const name = match?.[1] || match?.[2];
            if (name) fileName = decodeURIComponent(name);
          }

          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return {
            message: `Downloaded ${fileName}`,
            fileId,
          };
        } else {
          return {
            message: "Form submitted, but no file received.",
            fileId,
          };
        }
      }
      return {
        message: "Form submitted successfully.",
        fileId,
      }
    }

  } catch (error) {

    throw error;
  }
}

export async function submitOfflineForm(
  fileId: string,
  data: string
): Promise<SaveFormResponse> {
  try {
    // const payload = { fileId, filledValues: sectionInput };
    const ACRO_INTELLIGENCE_URL = import.meta.env.VITE_PDF_INTELLIGENCE_URL || 'https://pdf-intelligence-kit.genesis.dev.platform.autonomize.ai';
    const response = await apiClient.post<Blob>(
      `${ACRO_INTELLIGENCE_URL}/api/submit/${encodeURIComponent(fileId)}?data=${encodeURIComponent(data)}`,
      {},
      { responseType: "blob" }
    );
    console.log(response);
    return {
      message: "Form submitted successfully.",
      fileId,
    }

  } catch (error) {

    throw error;
  }
}


export async function updateFormStatus(
  htmlId: string | undefined,
  emailId: string,
  IsPending: boolean
): Promise<{ message: string }> {
  try {
    const response = await apiClient.put("/user/fileAssignment/updateStatus", {
      htmlId: htmlId,
      EmailId: emailId,
      IsPending: IsPending
    });
    return response.data;
  } catch (error) {

    throw error;
  }
}

export async function publishForm(
  fileId: string,
  emailId: string,
  isPublished: boolean
): Promise<{ message: string }> {
  try {
    const response = await apiClient.put("/PDFConvertor/file/updateStatus", {
      id: fileId,
      emailId: emailId,
      isPublished: isPublished
    });
    return response.data;
  } catch (error) {

    throw error;
  }
}

// Fallback list response retained for reference (now unused after simulating errors)
/* const fallbackTemplatesResponse: TemplatesResponse = {
  message: "HTML info retrieved successfully.",
  count: 3,
  data: [
    {
      id: "690e073752439d89781d13eb",
      userEmail: "admin@company.com",
      fileTitle: "Community First Choice Assessment",
      fileName: "sample-xfa-file.pdf",
      createdAt: "2025-11-07T14:50:31.365Z",
      fileId: "ce0e907b-663a-49ef-9fea-f3c685fb7864",
      htmlFileId: {
        timestamp: 1762527031,
        creationTime: "2025-11-07T14:50:31Z",
      },
      published: "Published",
    },
    {
      id: "690e078552439d89781d13ee",
      userEmail: "admin@company.com",
      fileTitle: null,
      fileName: "Financial_Release_V1.PDF",
      createdAt: "2025-11-07T14:51:49.851Z",
      fileId: "890fbb99-769c-42f5-8245-7e8ea45916ae",
      htmlFileId: {
        timestamp: 1762527109,
        creationTime: "2025-11-07T14:51:49Z",
      },
      published: "Published",
    },
    {
      id: "691070095e547aaa7906b3c3",
      userEmail: "admin@company.com",
      fileTitle: null,
      fileName: "cmp_xfa_example_filled.pdf",
      createdAt: "2025-11-09T10:42:17.87Z",
      fileId: "ca6bc9e3-376f-4b10-a1c6-8e378ac704ed",
      htmlFileId: {
        timestamp: 1762684937,
        creationTime: "2025-11-09T10:42:17Z",
      },
      published: "Published",
    },
  ],
}; */

// Fallback response retained for reference (now unused after simulating errors)
/* const fallbackTemplateDetails: TemplateDetailsResponse = {
  message: "Fallback template loaded.",
  data: {
    fileId: "fallback",
    userEmail: "",
    fileTitle: "Preview Unavailable",
    htmlContent:
      '<div style="padding:1rem;font-family:Arial,sans-serif;color:#333;"><h2>Preview unavailable</h2><p>We couldn\'t load the selected template. Please try again later.</p></div>',
  },
}; */

/**
 * Fetches templates from the API endpoint /PDFConvertor/${email}
 * @param email - User email to fetch templates for
 * @returns Promise with templates response
 */
export async function fetchTemplates(
  email: string
): Promise<TemplatesResponse> {
  // Check if online first

  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  // If OFFLINE: Load from IndexedDB only

  if (false) {
    try {
      const localData = await getAllForms();

      if (localData.data.length > 0) {
        return localData;
      } else {
        return {
          message: "No data available offline",
          count: 0,
          data: [],
        };
      }
    } catch (error) {
      console.log(error);
      return {
        message: "Error loading offline data",
        count: 0,
        data: [],
      };
    }
  }

  // If ONLINE: Fetch from API
  try {
    const response = await apiClient.get<TemplatesResponse>(
      `/PDFConvertor/info/getAllHtml/${encodeURIComponent(email)}`
    );
    console.log("Fetched templates from API:");
    const base = (response.data.data ?? []).map((t) => ({
      ...t,
      published: "Published",
      pdfType: t.isAcroFile ? "ACROFORM_PDF" : "XFA_PDF",
    }));

    const enriched: TemplatesResponse = {
      ...response.data,
      data: base,
    };

    // Save to IndexedDB

    // await SaveListData(enriched).catch((error) => {
    //   console.log(error);
    // });

    // Fetch and save detailed form data for each template
    const enableOffline = false;
    if (enableOffline) {
      for (const listItem of enriched.data) {
        try {

          const formDetails = await fetchTemplateDetailsForOffline(listItem.fileId, email);

          await SaveFormDetail(formDetails);
          const keyValueData = await getTemplateFormDataForOffline(listItem.fileId, email, listItem.fileName);
          await SaveFormKeyData(keyValueData);
          console.log("Fetched templates from API: offline data saved.");
        } catch (error) {
          console.log(error);
        }
      }
    }
    return enriched;
  } catch (error) {
    console.log(error);

    // If API fails while online, try to return cached data as fallback

    // try {
    //   const localData = await getAllForms();
    //   if (localData.data.length > 0) {

    //     return localData;
    //   }
    // } catch (cacheError) {
    //   console.log(cacheError);
    // }

    return {
      message: "No data available",
      count: 0,
      data: [],
    };
  }
}

/**
 * Fetches member-assigned templates from /user/assigned-html/{email}
 * Only used for member role. Returns MemberTemplatesResponse.
 */
export async function fetchMemberAssignedTemplates(
  email: string
): Promise<TemplatesResponse> {
  if (!email) throw new Error("Email is missing");
  try {
    const res = await apiClient.get<TemplatesResponse>(
      `/user/assigned-html/${encodeURIComponent(email)}`
    );
    return res?.data ?? { message: "", count: 0, data: [] };
  } catch (error) {

    throw error;
  }
}

/**
 * Fetches template details from /PdfConvertor/reformed/html/${fileId}
 * @param fileId - Template file ID
 * @returns Promise with template details response
 */
export async function fetchTemplateDetails(
  fileId: string,
  userEmail: string
): Promise<TemplateDetailsResponse> {
  if (!fileId) {
    throw new Error("File ID is missing");
  }

  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;



  // If OFFLINE: Load from IndexedDB only
  if (false) {

    try {
      const cachedDetail = await getFormDetail(fileId, userEmail);

      if (cachedDetail) {

        return cachedDetail;
      } else {

        throw new Error("Device is offline and no cached data available");
      }
    } catch (error) {

      throw error;
    }
  }

  // If ONLINE: Fetch from API


  try {
    const response = await apiClient.get<TemplateDetailsResponse>(
      `/PdfConvertor/reformed/html/${encodeURIComponent(fileId)}/${encodeURIComponent(userEmail)}`
    );

    return response.data;
  } catch (error) {


    // If API fails while online, try to return cached data as fallback

    // try {
    //   const cachedDetail = await getFormDetail(fileId, userEmail);
    //   if (cachedDetail) {

    //     return cachedDetail;
    //   }
    // } catch (cacheError) {
    //   console.log(cacheError);
    // }

    throw error;
  }
}


export async function fetchTemplateDetailsForOffline(
  fileId: string,
  userEmail: string
): Promise<TemplateDetailsResponse> {
  if (!fileId) {
    throw new Error("File ID is missing");
  }

  try {
    const response = await apiClient.get<TemplateDetailsResponse>(
      `/PdfConvertor/reformed/html/${encodeURIComponent(fileId)}/${encodeURIComponent(userEmail)}`
    );
    return response.data;
  } catch (error) {

    // Simulate propagation of the error; comment instead of deleting
    // return fallbackTemplateDetails;
    throw error;
  }
}

/**
 * Fetches saved form data for a given template from
 * GET /PDFConvertor/reformed/html/forms/:templateId
 * @param templateId - Template ID
 * @returns Promise with form data response
 */
export async function getTemplateFormData(
  templateId: string,
  emailId: string,
  fileName: string
): Promise<TemplateFormDataResponse> {
  if (!templateId) {
    throw new Error("Template ID is missing");
  }

  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  // If OFFLINE: Load from IndexedDB

  if (false) {

    try {
      // First, try to get saved form data (draft/unsaved changes)
      const savedResponse = await getSavedFormDataByFileId(templateId);

      if (savedResponse) {

        return savedResponse;
      }

      // If no saved data, fall back to original form key data

      const response = await getFormKeyData(templateId);


      if (response) {

        return response;
      } else {

        throw new Error("Device is offline and no cached form data available");
      }
    } catch (error) {

      throw error;
    }
  }

  // If ONLINE: Fetch from API
  
  try {
    const response = await apiClient.get<TemplateFormDataResponse>(
      `/PDFConvertor/reformed/html/forms/${encodeURIComponent(templateId)}/${encodeURIComponent(emailId)}/${encodeURIComponent(fileName)}`
    );
    return response.data;
  } catch (error) {

    throw error;
  }
}

export async function getTemplateFormDataForOffline(
  templateId: string,
  emailId: string,
  fileName: string
): Promise<TemplateFormDataResponse> {
  if (!templateId) {
    throw new Error("Template ID is missing");
  }
  try {
    const response = await apiClient.get<TemplateFormDataResponse>(
      `/reformed/html/forms//${encodeURIComponent(templateId)}/${encodeURIComponent(emailId)}/${encodeURIComponent(fileName)}`
    );
    return response.data;
  } catch (error) {

    throw error;
  }
}




async function SaveListData(data: TemplatesResponse) {
  // Save each form from the response to IndexedDB
  for (const form of data.data) {
    await saveItem("forms", {
      id: form.id,
      htmlId: form.htmlId,
      userEmail: form.userEmail,
      fileTitle: form.fileTitle,
      fileName: form.fileName,
      createdAt: form.createdAt,
      updatedAt: form.updatedAt,
      fileId: form.fileId,
      htmlFileId: form.htmlFileId,
      isAcroFile: form.isAcroFile,
      isPublished: form.isPublished,
      published: form.published,
      pdfType: form.pdfType,
    });
  }

}

async function SaveFormDetail(data: TemplateDetailsResponse) {
  // Save each form from the response to IndexedDB
  const form = data.data;
  await saveItem("formData", {
    id: form.fileId,
    userEmail: form.userEmail,
    htmlContent: form.htmlContent,
    fileTitle: form.fileTitle,
    fileId: form.fileId,
    isPublished: form.isPublished,
    isPending: form.isPublished,
  });

}

async function SaveFormKeyData(data: TemplateFormDataResponse) {
  // Save each form from the response to IndexedDB

  await saveItem("formKeyData", {
    id: data.fileId,
    data: data.data
  });

}

// Function to retrieve form key data from IndexedDB based on fileId
export async function getFormKeyData(fileId: string): Promise<TemplateFormDataResponse | null> {
  try {
    const allFormKeyData = await getAll("formKeyData");


    // Find the form key data that matches the fileId
    const formKeyData = allFormKeyData.find((form: any) =>
      form.id === fileId
    );

    if (formKeyData) {

      return {
        message: "Form key data retrieved from IndexedDB",
        fileId: formKeyData.id,
        data: formKeyData.data
      };
    }


    return null;
  } catch (error) {

    throw error;
  }
}

// Function to retrieve all forms from IndexedDB
export async function getAllForms(): Promise<TemplatesResponse> {
  try {
    const forms = await getAll("forms");
    return {
      message: "Forms retrieved from IndexedDB",
      count: forms.length,
      data: forms as Template[],
    };
  } catch (error) {

    throw error;
  }
}

// Function to retrieve form detail from IndexedDB based on fileId
export async function getFormDetail(fileId: string, userEmail: string): Promise<TemplateDetailsResponse | null> {
  try {
    const allFormData = await getAll("formData");

    // Find the form that matches both fileId and userEmail

    const formDetail = allFormData.find((form: any) =>
      form.fileId === fileId && form.userEmail === userEmail
    );

    if (formDetail) {

      return {
        message: "Form detail retrieved from IndexedDB",
        data: formDetail as TemplateDetails
      };
    }


    return null;
  } catch (error) {

    throw error;
  }
}


async function SubmitFormData(data: any, fieldId: string, type: string) {
  // Save each form from the response to IndexedDB

  await saveItem("submittedFormKeyData", {
    id: fieldId,
    data: data,
    type: type
  });

}

async function SaveFormData(data: any, fieldId: string) {
  // Save each form from the response to IndexedDB

  await saveItem("saveFormKeyData", {
    id: fieldId,
    data: data,
  });

}



export async function getAllSavedFormData(): Promise<any[]> {
  try {
    const allData = await getAll("saveFormKeyData");

    return allData;
  } catch (error) {

    throw error;
  }
}

/**
 * Retrieves saved form data from saveFormKeyData store by fileId
 * @param fileId - The file ID to retrieve
 * @returns Promise with saved form data in TemplateFormDataResponse format, or null if not found
 */
export async function getSavedFormDataByFileId(fileId: string): Promise<TemplateFormDataResponse | null> {
  try {
    const allData = await getAll("saveFormKeyData");
    const savedFormData = allData.find((form: any) => form.id === fileId);

    if (savedFormData) {

      return {
        message: "Saved form data retrieved from IndexedDB",
        fileId: savedFormData.id,
        data: {
          formData: JSON.stringify(savedFormData.data.data)
        }
      };
    }


    return null;
  } catch (error) {

    throw error;
  }
}

export async function getAllSubmittedFormData(): Promise<any[]> {
  try {
    const allData = await getAll("submittedFormKeyData");

    return allData;
  } catch (error) {

    throw error;
  }
}

/**
 * Deletes saved form data from saveFormKeyData store by fileId
 * @param fileId - The file ID to delete
 * @returns Promise<boolean> - True if deleted successfully, false otherwise
 */
export async function deleteSavedFormData(fileId: string): Promise<boolean> {
  try {
    const deleted = await deleteItem("saveFormKeyData", fileId);
    if (deleted) {
      // Deleted successfully
    } else {
      // else case
    }
    return deleted;
  } catch (error) {

    throw error;
  }
}

/**
 * Deletes submitted form data from submittedFormKeyData store by fileId
 * @param fileId - The file ID to delete
 * @returns Promise<boolean> - True if deleted successfully, false otherwise
 */
export async function deleteSubmittedFormData(fileId: string): Promise<boolean> {
  try {
    const deleted = await deleteItem("submittedFormKeyData", fileId);
    if (deleted) {
      // Deleted successfully
    } else {
      // else case
    }
    return deleted;
  } catch (error) {

    throw error;
  }
}

